import paymentRecord from '@/translations/en/payment-record';
import paymentsList from '@/translations/en/payments-list';
import unmatchedProofs from '@/translations/en/unmatched-proofs';

export default {
  tabs: {
    accountsPayable: 'Accounts payable',
    paymentRequests: 'Payment requests',
    payments: 'Payments',
    credits: 'Credits',
  },
  // PE-1004: the payments surface's frame and its counter band.
  paymentsSurface: {
    contentTabs: {
      label: 'Payments sections',
      payments: 'Payments',
      unmatchedProofs: 'Unmatched proofs',
    },
    band: {
      unavailable: 'unavailable',
      countUnavailable: 'count unavailable',
      cellName: '{label}: {figure}. {action}',
      total: {
        label: 'Total payments',
        meaning: 'Every payment in the organisation, apart from cancelled ones.',
        action: 'Clears the filters and lists every payment.',
      },
      confirmed: {
        label: 'Confirmed',
        meaning: 'Payments confirmed as paid.',
        figure: '{confirmed} of {total}',
        action: 'Filters the table to confirmed payments.',
      },
      awaitingProof: {
        label: 'Awaiting proof',
        meaning: 'Payments logged with their proof of payment to follow, still waiting for it.',
        oldest: 'oldest {count} day | oldest {count} days',
        oldestWaited: 'The oldest has waited {count} day. | The oldest has waited {count} days.',
        pastWindow: 'That is past the matching window.',
        nothingWaiting: 'nothing waiting',
        figure: '{count}, {age}',
        agePastWindow: '{age}, past the window',
        action: 'Filters the table to payments awaiting proof.',
      },
      blocked: {
        label: 'Blocked',
        meaning:
          'Payments a proof matched but could not confirm, waiting for someone to resolve them.',
        action: 'Filters the table to blocked payments.',
      },
      unmatchedProofs: {
        label: 'Unmatched proofs',
        action: 'Opens the Unmatched proofs tab.',
      },
    },
  },
  // Vendor account payment terms — used across the vendor form, payment requests, and orders
  // (see getPaymentTermDisplayName / getPaymentTermDescription in accounts.utils.ts).
  paymentTerms: {
    labels: {
      monthEnd: 'Month End',
      thirtyDay: '30 Day',
      sixtyDay: '60 Day',
      ninetyDay: '90 Day',
      depositAccount: 'Deposit Account',
    },
    // Secondary line shown beneath each payment-period option in the vendor form dropdown.
    descriptions: {
      monthEnd: 'Due at the next vendor accounts payment run',
      thirtyDay: 'Due 1 month after the organisation’s scheduled vendor payment date',
      sixtyDay: 'Due 2 months after the organisation’s scheduled vendor payment date',
      ninetyDay: 'Due 3 months after the organisation’s scheduled vendor payment date',
      depositAccount: 'Prepaid funds; orders are deducted from the balance',
    },
  },
  accountsPayable: {
    noContent:
      'You currently have no accounts owing.\n Generate payment requests from approved orders',
    budgetInfoItems: {
      approvedCodDueNow: 'Due now',
      approvedCodDueNowTooltip:
        'All approved and unpaid COD payments that are overdue or due today',
      approvedCodToMonthEnd: 'Adhoc due',
      approvedCodToMonthEndTooltip:
        'All approved and unpaid COD payments that are due between tomorrow and the end of the month',
      approvedAccountDueNext: 'Accounts due',
      approvedAccountDueNextTooltip:
        'All account type payments for approved and unpaid PRs that are due before the end of the month',
      pendingPayments: 'Pending approval',
      pendingPaymentsTooltip: 'The total of all PRs sitting in a pending or verified state',
      totalCredit: 'Total credit',
      totalCreditTooltip: 'The total credit you have across all vendors',
      totalCreditTooltipByVendor: 'The total credit you have with this vendor',
      pendingTotal: 'Pending amount payable',
      pendingPaymentRequests: 'Pending payment requests',
    },
    filters: {
      search: 'Search by vendor',
      status: 'Status',
    },
    tableColumns: {
      vendor: 'Vendor',
      status: 'Status',
      nextDueDate: 'Next due',
      dueNow: 'Due now',
      currentPayrun: 'Current account',
      pendingApprovals: 'Pending Approvals',
      totalApproved: 'Total Approved',
      credit: 'Vendor credit',
      dueDate: 'Due date',
    },
    statuses: {
      PRsPending: 'PRs Pending',
      current: 'Current',
      credits: 'Credit',
      settled: 'Settled',
    },
    contextMenu: {
      viewPending: 'View pending payments',
      viewApproved: 'View approved payments',
      logPayment: 'Log payment',
    },
    accountPayable: {
      noData: 'No payments have been made for this account.',
      tabs: {
        orders: 'Orders',
        claims: 'Claims',
        returns: 'Returns',
        credits: 'Credits',
        payments: 'Payments',
        // The per-vendor tab's label with the payment lifecycle flag on (FR-P1); `payments` stays
        // for flag-off, since a translation value cannot read a flag.
        paymentRequests: 'Payment requests',
        statements: 'Statements',
        paymentHistory: 'Payment History',
        transactionLog: 'Transaction Log',
      },
      orders: {
        summaryItems: {
          order: 'order',
          claim: 'claim',
          orders: 'orders',
          claims: 'claims',
          requestedOrders: 'Requested {type}',
          pendingOrderValue: 'Pending {type} value',
          approvedOrderValue: 'Approved {type} value',
          inclVat: '{amount} incl vat',
        },
      },
      newPayment: 'New Payment',
      logPayment: 'Log payment',
      adhocPayment: 'Create adhoc payment',
      logIndividualPayment: 'Log individual payment',
      logDueNow: 'Log due now',
      logAccountDue: 'Log account due',
      view: 'View',
      summaryItems: {
        total: 'Total paid to vendor',
        current: 'Total paid during current period',
      },
      filters: {
        search: 'Search by payment request ID',
        dateRange: 'Date range',
      },
      tableColumns: {
        paymentDate: 'Payment Date',
        paymentRequests: 'Payment requests',
        paid: 'Amount paid',
        credit: 'Credit applied',
        proofOfPayment: 'Proof of payment',
      },
      logPaymentSidebar: {
        cannotLogPayment:
          'Payments can only be logged for accounts with approved payment requests.',
        title: 'Log payment',
        selectPaymentRequests: 'Select payments included in this log',
        credit: 'Credit',
        total: 'Total: ',
        totalOwed: 'Total owed',
        applyCredit: 'Apply vendor credit on this payment',
        creditRemaining: '{amount} Credit Remaining',
        paymentDetails: {
          title: 'Payment Amount',
          subtitle: 'Complete payment amount and upload proof of this payment',
        },
        creditAmount: 'Enter credit amount',
        paymentAmount: 'Enter payment amount',
        proofOfPayment: 'Proof of payment',
        uploadProofOfPayment: 'Upload proof of payment',
        confirm: 'Confirm Payment',
        logPaymentSuccess: {
          title: 'Payment logged',
          content: 'Payment logged successfully',
        },
        prHasLinkedReturn:
          'This payment request is tied to an order with a pending return. The value of this payment may change based on the outcome of the return.',
        tableColumns: {
          checkbox: 'checkbox',
          paymentId: 'Payment ID',
          paymentMethod: 'Payment method',
          dueDate: 'Due date',
          amountInclVat: 'Amount incl. VAT',
        },
        amountSelected: '{number} selected',
        confirmPaymentLog: 'Confirm Payment Log',
        vendorPaymentDetails: {
          title: 'Vendor Payment Details',
          noDetails:
            'No vendor details captured. Please update vendor payment details in your vendors.',
          accountHolder: 'Account Holder: ',
          bank: 'Bank: ',
          accNumber: 'Acc Number: ',
          branchCode: 'Branch Code: ',
        },
        paymentSummary: {
          title: 'Payment Summary',
          totalExclVat: 'Total Excl VAT',
          vat: 'VAT Total',
          totalInclVat: 'Total Incl VAT',
        },
        paymentLog: {
          title: 'Payment Log',
          totalOwing: 'Total Owing',
          creditApplied: 'Credit Applied',
        },
        amountToTransfer: 'Amount to transfer',
        createPendingPayment: 'Create payment',
        phone: {
          instructions:
            'To log a payment on your phone, open an approved payment request and choose Log payment.',
          viewRequests: 'View payment requests',
        },
        outcomeUnconfirmed:
          'We could not confirm whether this payment was created. Check the payments list before trying again: logging it a second time would create a duplicate.',
        channel: {
          heading: 'How will the proof of payment arrive?',
          upload: {
            label: 'Proof supplied now',
            description: 'You have the proof of payment and can attach it to this log.',
          },
          email: {
            label: 'Proof to follow by email',
            description:
              'The payment waits until the bank emails its confirmation to your proof address.',
          },
        },
        emailChannel: {
          instructions: {
            order:
              'Create the payment first, then make the transfer. Your reference is only generated once the payment exists.',
            exactAmount:
              'Transfer exactly this amount. Any difference has to be settled by hand before the payment can confirm.',
          },
        },
        transferReference: {
          created: 'Payment created',
          awaiting:
            'It is waiting for its proof of payment. Use these details for the transfer, then have the bank send the confirmation to the address below.',
          amountUnavailable: 'Unavailable',
          reference: {
            label: 'Payment reference',
            description: "Put this in your bank's reference field.",
          },
          beneficiary: {
            label: 'Beneficiary',
            bank: 'Bank',
            branchCode: 'Branch code',
            accountNumber: 'Account number',
          },
          amount: {
            label: 'Amount to transfer',
            description: 'Transfer exactly this amount. Any difference goes to manual review.',
          },
          proofAddress: {
            label: 'Proof of payment address',
            description: 'Add this as the confirmation recipient on the transfer.',
          },
          recovery:
            'Wrong beneficiary or wrong amount? Cancel this payment from its record, then log it again. Cancelling returns the payment requests to payable.',
          viewAwaitingProof: 'Go to payments awaiting proof',
          close: 'Close',
        },
        return: 'Return',
        enterAmountPaid: 'Enter amount paid',
        noAttachments: 'No attachments added',
        // PE-1020: the upload channel's proof check (flow 1, the Comparison group; DL-186).
        comparison: {
          checking: 'Checking this proof against the payment.',
          holdNote: 'Confirm Payment is available once the check finishes.',
          updating: 'Updating the comparison for the new amount.',
          failed:
            'We could not check this proof just now. You can still confirm: it will be checked as the payment is created, and if the amounts disagree the payment will be recorded as Confirmed on a disagreeing amount.',
          agrees: 'This proof agrees with the payment.',
          amountDisagrees:
            'The amount on this proof does not agree with the payment. You can still confirm.',
          beneficiaryDoesNotFit: 'The beneficiary on this proof does not fit the vendor.',
          beneficiaryNotJudged: 'No beneficiary name could be read, so it could not be judged.',
          notRead:
            'No amount could be read from this proof, so it could not be compared. You can still confirm.',
          unsupportedType:
            'This file is a {type} file, which cannot be read, so it could not be compared. You can still confirm.',
          notChecked:
            'This proof could not be checked: reading documents is unavailable just now. You can still confirm. Nothing will be flagged, and the payment will record that its amount could not be checked.',
          alreadyHeld: {
            PaymentProof:
              'This document is already held as the proof of {reference}. Check it is the right proof before you confirm.',
            PaymentProofUnnamed:
              'This document is already held as the proof of another payment. Check it is the right proof before you confirm.',
            UnmatchedProof:
              'This document is already on unmatched proofs, waiting to be attached. Check it is the right proof before you confirm.',
            RejectedProof:
              'This document is already held as a rejected proof. Check it is the right proof before you confirm.',
          },
          severalPayments:
            'This document appears to cover more than one payment. Check it is the proof for this payment alone before you confirm.',
          flagNote:
            'If you confirm, this payment will be recorded as Confirmed on a disagreeing amount, with these figures. No reason is needed.',
          beneficiary: {
            fits: 'Beneficiary on the proof: {name}. It fits the vendor, {vendor}.',
            fitsUnnamedVendor: 'Beneficiary on the proof: {name}. It fits the vendor.',
            neither:
              'Beneficiary on the proof: {name}. It neither fits nor contradicts the vendor, {vendor}.',
            neitherUnnamedVendor:
              'Beneficiary on the proof: {name}. It neither fits nor contradicts the vendor.',
            onProof: 'Beneficiary on the proof',
            vendor: 'Vendor',
            notRecorded: 'Not recorded',
            checkProof: 'Check this is the proof for this payment. You can still confirm.',
          },
        },
        fullyCovered: {
          title: 'Fully covered by vendor credit.',
          body: 'No cash moves and no proof of payment is needed: the payment will confirm on submit.',
        },
        invalidCreditAmount:
          'The entered credit amount exceeds the available credit for this vendor',
        creditExceedsTotal: 'The entered credit amount exceeds the total amount owing',
        linkedReturnsModal: {
          title: 'Pending returns',
          content1:
            'One or more payments you have selected are linked to orders with pending return requests. These payment amounts may change based on the outcome of the return.',
          severityMessage:
            'You are paying the full amount for these order payments, which are likely to reduce when the returns are approved.',
          content2:
            'If you would rather remove the payments with pending order refunds, and pay for the remaining selected, select',
          content3: '‘Continue without payments’',
          or: 'or',
          selectCreditType: 'Please select what happens to these return refunds upon approval.',
          buttons: {
            continue: 'Continue',
            withoutPayments: 'Continue without payments',
          },
        },
      },
      adhocPaymentSidebar: {
        title: 'Create Adhoc Payment',
        details: {
          title: 'Payment request details',
          subtitle: 'Complete payment details below. Required fields marked by *',
        },
        paymentNote: 'Add a payment note',
        create: 'Create Payment Request',
        fields: {
          method: 'Method',
          methodPlaceholder: 'Payment method',
          dueDate: 'Due date',
          assignee: 'Assigned to',
          assigneePlaceholder: 'Assignee',
        },
        createSuccess: {
          title: 'Adhoc payment request created',
          content: 'Adhoc payment request created successfully',
        },
      },
    },
  },
  paymentRequests: {
    noContent:
      'You currently have no payment requests. \n Generate payment requests from approved orders',
    noApprovedPrs: 'You have no approved payment requests for this vendor.',
    pushedBack: {
      badge: 'Pushed back',
      filterLabel: 'Pushed back',
      bannerHeading: 'Pushed back',
      byLabel: 'Pushed back by',
      by: 'By {name} · {date}',
    },
    prTypes: {
      all: 'All',
      orderPayments: 'Order Payments',
      claimPayments: 'Claim Payments',
      depositRequests: 'Deposit Requests',
      retentions: 'Retentions',
      advancedRequests: 'Advanced Payments',
    },
    budgetInfoItems: {
      currentPeriod: 'Current period total',
      numberPlusDays: '{number}+ day total',
    },
    filters: {
      search: 'Search by payment ID',
      site: 'Site',
      vendor: 'Vendor',
      period: { title: 'Period', current: 'Current', days: '{number}+ Days' },
      status: 'Status',
    },
    details: {
      title: 'Payment Request',
      paymentLogged: {
        label: 'Payment logged',
        reference: 'Reference',
        copyReference: 'reference',
        openPayment: 'Open payment {reference}',
        amountAndDate: '{amount} paid on {date}',
        settledWithOthers:
          'Settled with 1 other request on this payment. | Settled with {n} other requests on this payment.',
        proofNotArrived: 'Proof of payment has not arrived yet: the payment is waiting on it.',
        unavailable: 'The payment for this request could not be loaded.',
        retry: 'Retry',
        retryLabel: 'Retry loading the payment',
        retryFailed: 'Retry failed. The payment for this request still could not be loaded.',
        loaded: 'Payment loaded: {reference}.',
        none: 'No payment has been logged for this request.',
      },
      paymentSummary: {
        title: 'Payment Summary',
        subtitle: 'Payment outline for this request',
        paymentBreakdown: 'Payment breakdown',
        order: 'Order',
        totalInclVat: 'Total Incl VAT',
        totalExclVat: 'Total Excl VAT',
        vat: 'VAT Total',
        approvals: 'Approvals',
        reason: 'Reason for decline',
        adhoc: 'Adhoc payment',
        paymentNote: 'Payment note',
        creditNotes: 'Credit notes',
        credit: 'Credit',
        returnAdjustment: 'Return adjustment',
        paymentFor: 'Payment for',
        orderAdjustment: 'Order log adjustment',
      },
      orderSummary: {
        title: 'Order Summary',
        subtitle: 'Order details related to this request',
        orderDetails: 'Order details',
        vendor: 'Vendor',
        siteName: 'Site name',
        deliveryDate: 'Delivery date',
        notes: 'Notes',
        documentsTitle: 'Order documentation',
        documentsPlaceholder: 'No documents uploaded',
      },
      documentation: {
        title: 'Documentation',
        subtitle: 'All documents related to this request',
      },
      approve: 'Approve request',
      approveShort: 'Approve',
      approveWhileProcessing: 'Still processing the previous payment request',
      verify: 'Verify request',
      verifyShort: 'Verify',
      decline: 'Decline request',
      declineShort: 'Decline',
      pushBack: 'Push back',
      verifyModal: {
        title: 'Verify this payment request?',
        body: 'This will mark the payment request as Verified and send it to the Org Finance Officers for approval.',
        notesLabel: 'Notes (optional)',
        confirm: 'Verify payment request',
        cancel: 'Cancel',
      },
      approveModal: {
        title: 'Approve this payment request?',
        body: 'This will approve the payment request and make it ready for payment. Nothing is paid until a payment is logged against it.',
        notesLabel: 'Notes (optional)',
        confirm: 'Approve payment request',
        cancel: 'Cancel',
      },
      deleteDocumentModal: {
        title: 'Delete this document?',
        body: 'The document is removed from this payment request.',
        warning: 'This cannot be undone.',
        confirm: 'Delete document',
        cancel: 'Cancel',
      },
      awaitingVerification: 'Awaiting verification by Site Finance Officer',
      // A bulk request has no site, so its verifier is the org finance admin (DL-236 D-15).
      awaitingVerificationBulk: 'Awaiting verification by Org Finance Admin',
      verifyValidation: {
        message: 'Before verifying, set the {fields}.',
        fields: {
          assignee: 'payment assignee',
          dueDate: 'due date',
        },
      },
      approveValidation: {
        // `fields` is a list built at runtime, so the names stay lowercase and article-free:
        // they read correctly whether one, two or all three are missing.
        message: 'Before approving, set the {fields}.',
        fields: {
          method: 'payment method',
          dueDate: 'due date',
          assignee: 'payment assignee',
        },
      },
      pushBackModal: {
        title: 'Push back this payment request?',
        body: 'This will return the payment request to the Site Finance Officers for re-verification, and change its status back to Requested. They will be emailed your reason.',
        bodyBulk:
          'This will return the payment request to the Org Finance Admins for re-verification, and change its status back to Requested. They will be emailed your reason.',
        reasonLabel: 'Reason for pushing back',
        required: 'Add a reason to push back this payment request.',
        confirm: 'Push back',
        cancel: 'Cancel',
      },
      declineModal: {
        title: 'Decline payment request',
        body: 'Declining this payment request will cancel the order and any other linked payment requests. This cannot be undone. Please provide a reason.',
        placeholder: 'Write your reason here',
        confirm: 'Yes, cancel order',
        cancel: 'No, go back',
      },
    },
    tableColumns: {
      paymentId: 'Payment ID',
      status: 'Status',
      vendor: 'Vendor',
      paymentMethod: 'Payment method',
      site: 'Site',
      date: 'Due date',
      amount: 'Amount',
      amountVat: 'Amount (Incl. VAT)',
    },
    statuses: {
      requested: 'Requested',
      approved: 'Approved',
      declined: 'Declined',
      paid: 'Paid',
      cancelled: 'Cancelled',
      verified: 'Verified',
    },
    contextMenu: {
      view: 'View',
      approve: 'Approve',
      decline: 'Decline',
    },
    statusUpdateSuccess: {
      title: 'Status updated',
      content: 'Payment request status has been successfully updated',
    },
    statusUpdateError: {
      title: 'Update failed',
      content: 'Something went wrong while updating the payment request. Please try again.',
    },
    // Declining a bulk payment request also cancels its bulk order (PE-1043). Only a request that
    // carries the `bulkOrder` marker reads these; every other decline keeps `details.declineModal`.
    // Verify, approve and push back on a bulk payment request (PE-1179).
    bulkAction: {
      notAvailable: 'This payment request is no longer available to you, so nothing was changed.',
    },
    bulkDecline: {
      // Context: shortened from "Decline and cancel bulk order" until the design system's Button
      // stops growing while it loads: the longer label pushed "No, go back" out of the dialog.
      confirm: 'Decline and cancel',
      warning: {
        heading: 'This also cancels bulk order {reference}',
        body: "Declining this payment request cancels bulk order {reference} and every site's order under it. Each site's BOQ commitment is released. This cannot be undone.",
      },
      reasonNote: "Your reason is recorded as the bulk order's cancellation reason.",
      success: {
        title: 'Payment request declined',
        content: 'Bulk order {reference} has been cancelled.',
      },
      landed: {
        title: 'Payment request declined',
        content:
          'The payment request is declined. Open bulk order {reference} to check its cancellation has finished.',
      },
      partial: {
        title: 'Payment request declined',
        content:
          "The payment request was declined, but cancelling bulk order {reference} has not finished. Some site orders are not cancelled yet. The bulk order's owner can finish the cancellation from the bulk order.",
      },
      errors: {
        changed:
          'This payment request changed since you opened it, so it has been reloaded. Check it and try again.',
        noResponse:
          'We could not reach the server, so we cannot tell whether this went through. The payment request has been reloaded.',
        refusedGeneric: 'This did not go through. The payment request has been reloaded.',
        notAvailable:
          'This payment request is no longer available to you, so it has not been declined.',
      },
    },
    // The site orders of a bulk payment request, in its Order Summary (PE-1175, S-09). Every label is
    // owed the design check (DL-198 A-04); "Value" is a site order's own total, as the product owner
    // asked for it (2026-10-14).
    bulkBreakdown: {
      columns: {
        site: 'Site',
        siteOrder: 'Site order',
        status: 'Status',
        value: 'Value',
      },
      notCounted: 'Not counted',
      notCountedReason: {
        returned: 'The site order was returned in full',
        cancelled: 'The site order was cancelled',
        rejected: 'The site order was rejected',
        other: 'This site order does not count',
      },
      // Information only: payment does not wait for deliveries (DL-231).
      delivery: {
        all: 'Delivered at every site',
        outstanding:
          '{outstanding} of {count} site not yet delivered | {outstanding} of {count} sites not yet delivered',
      },
      total: 'Bulk order total incl VAT',
      loadError: {
        title: "Couldn't load this bulk payment request",
        retry: 'Try again',
      },
      summary: {
        bulkOrder: 'Bulk order',
      },
    },
    bulkSiteCount: '{count} site | {count} sites',
    detailsUpdateSuccess: {
      title: '{type} updated',
      content: 'Payment request details have been updated',
    },
    detailsUpdateError: {
      title: '{type} not saved',
      content: 'Your change could not be saved. Try again.',
    },
    declineNotAllowed: {
      APR: "Declining is disabled because it was created by an action that can't be undone. If it seems incorrect, you can cancel the order.",
      delivered:
        'Declining is disabled once items from the order have been delivered. To proceed, you must return the delivered items and then cancel the order.',
      paid: 'Declining is disabled once an order has had a payment made. To proceed, you must cancel the order.',
      awaitingProof:
        'Declining is disabled while {payment} is awaiting proof for this order. To proceed, upload its proof or cancel the payment.',
      // Only someone who logs payments can upload a payment's proof or cancel it (FR-L10).
      awaitingProofNoLogPayment:
        'Declining is disabled while {payment} is awaiting proof for this order. To proceed, someone who logs payments must upload its proof or cancel it.',
    },
    approveNotAllowed: {
      orderNotDelivered:
        'Approval is disabled until the linked order has been fully delivered. Please ensure all items are delivered before approving this payment request.',
    },
  },
  transactionLog: {
    noContent:
      'Your transaction log is currently empty. Start generating some transactions to see the logs here.',
    filters: {
      search: 'Search',
    },
    tableColumns: {
      date: 'Date',
      description: 'Description',
      referenceId: 'Reference ID',
      debit: 'Debit',
      credit: 'Credit',
      balance: 'Balance',
      id: 'ID',
    },
    toolTips: {
      debit: 'Increases the amount you owe the vendor.',
      credit: 'Decreases the amount you owe the vendor.',
      historicalPayment: `This records a payment made before full account tracking began. It's included for your reference to provide a more complete payment history.`,
      balanceAdjustment: 'An adjustment was made to correct the balance of this account.',
    },
  },
  accountSummary: {
    noStatements:
      'Statements will be generated on a monthly basis for vendors once orders have been placed. You can also manually generate statements using the “Generate statement” button.',
    generateStatement: 'Generate statement',
    statementDate: 'Statements are auto-generated on the {day} of each month.',
    tableColumns: {
      name: 'Name',
      createdOn: 'Created on',
      dateRange: 'Date range',
      vendorNotified: 'Vendor notified',
      generatedBy: 'Generated by',
    },
    download: 'Download',
    contextMenu: {
      sendToVendor: 'Send to vendor',
      deleteStatement: 'Delete statement',
    },
    filters: {
      createdOn: 'Created on',
      generatedBy: 'Generated by',
    },
    modal: {
      title: 'Generate account statement',
      subtitle: 'Select the date range to generate this account statement',
      to: 'to',
      shareSummary: 'I want to send this account summary to vendor/contractor on creation',
      generateNow: 'Generate now',
    },
    shareStatementModal: {
      title: 'Send statement to vendor',
      content:
        'This will send the selected statement summary to the vendor via email. Are you sure?',
      yesSend: 'Yes, send',
    },
    deleteStatementModal: {
      title: 'Delete statement',
      content: 'Are you sure you want to delete this statement?',
    },
    toasts: {
      generateSuccess: {
        title: 'Statement generated',
        content: 'Statement has successfully been generated.',
      },
      generateFailed: {
        title: 'Generate statement failed',
        content: 'Oh no. Looks like the statement failed to generate. Please try again.',
      },
      downloadSuccess: {
        title: 'Download statement',
        content: 'Statement downloaded successfully.',
      },
      deleteSuccess: {
        title: 'Statement deleted',
        content: 'Statement has successfully been deleted.',
      },
      shareSuccess: {
        title: 'Statement sent to vendor',
        content: 'Statement has successfully been sent to the vendor.',
      },
    },
  },
  creditCheck: {
    creditCheckWarning: `This vendor account's credit is lower than the required amount to pay approved payments. Please add more credit using the log adhoc payment option.`,
    creditCheckWarningButtonText: 'Load credit',
    creditAvailable: 'Credit available:',
    creditRequired: 'Credit required: {reqAmount}',
  },
  paymentLog: {
    title: 'Payment Log - {paymentNumber}',
    payment: 'Payment',
    totalPaymentLog: 'Total Payment Log',
    creditUsed: 'Credit Used',
    totalPaid: 'Total Paid',
    proofOfPayment: 'Proof of Payment',
    tableColumns: {
      id: 'ID',
      amountInclVat: 'Amount incl VAT',
    },
  },
  paymentRecord,
  // PE-1007: the Payments content tab's table.
  paymentsList,
  unmatchedProofs,
  creditRequests: {
    credit: 'Credit',
    refund: 'Refund',
    summary: {
      title: '{type} Summary',
      subtitle: 'Payment outline for this request',
      details: '{type} Details',
      type: '{type} type',
      orderNo: 'Order no.',
      bulkOrder: 'Bulk order',
      siteName: 'Site name',
      vendorDetails: 'Vendor Details',
      vendorName: 'Vendor name',
      vendorEmail: 'Vendor email',
      history: '{type} History',
      requestedBy: 'Requested by',
      reviewedBy: 'Reviewed by',
      reasonForCancel: 'Reason for cancellation',
      notes: 'Notes',
    },
    breakdown: {
      title: '{type} Breakdown',
      subtitle: 'Items included in this {type}',
      return: '{type} for return',
      order: '{type} for order',
      bulkOrder: 'Bulk order',
      adhocPayment: '{type} for adhoc payment',
      creditLogged: 'Credit logged',
    },
    completeOrDoc: {
      complete: 'Complete this {type}',
      documentation: 'Documentation',
      attach: 'Attach',
      subtitle: 'All documentation added to this payment',
      fileAttachments: 'File attachments',
      enterAmountReceived: 'Enter the amount you received',
      amountReceivedPlaceholder: 'Enter amount received',
      proof: 'Provide your proof of {type}',
      creditValidation: 'The entered {type} amount differs from the return amount.',
      setToFullAmount: 'Set to full amount ({amount})',
    },
    buttons: {
      complete: 'Complete {type}',
      cancel: 'Cancel {type}',
      cancelNotAllowed:
        "You can't cancel this request because it was created by an action that can't be undone. If you do not expect to receive a credit or refund, then you can complete the request for R0",
    },
    cancelModal: {
      title: 'Cancel {type}',
      content: 'Are you sure you wish to cancel this {type}?',
      warning: 'Cancelling this {type} will also cancel the return {returnNumber} linked to it.',
      buttons: {
        cancel: 'Keep {type}',
        confirm: 'Yes, cancel {type}',
      },
    },
    completeModal: {
      title: 'Complete {type}',
      content: 'Are you sure you wish to complete this {type}?',
      buttons: {
        confirm: 'Yes, complete {type}',
      },
    },
    toasts: {
      cancelSuccess: {
        title: '{type} cancelled',
        content: '{type} has been successfully cancelled.',
      },
      cancelError: {
        title: 'Error cancelling {type}',
        content: 'An error occurred while cancelling the {type}',
      },
      completeSuccess: {
        title: '{type} completed',
        content: '{type} has been successfully completed.',
      },
      completeError: {
        title: 'Error completing {type}',
        content: 'An error occurred while completing the {type}',
      },
      uploadDocSuccess: {
        title: 'Proof of {type} uploaded',
        content: 'The document has been successfully uploaded',
      },
      uploadDocError: {
        title: 'Error uploading proof of {type}',
        content: 'An error occurred while uploading the document. Please try again.',
      },
    },
  },
};
